import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Database, Upload, Loader2, Download, ArrowDownUp, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const downloadWithAuth = async (url, filename) => {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) throw new Error("Error al descargar");
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

const AhrefsIntegrationTab = () => {
  const [sheets, setSheets] = useState([]);
  const [ahrefsSheets, setAhrefsSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [selectedAhrefs, setSelectedAhrefs] = useState("");
  const [merging, setMerging] = useState(false);
  const [sorting, setSorting] = useState(false);
  const [uploadingSheet, setUploadingSheet] = useState(false);
  const [uploadingAhrefs, setUploadingAhrefs] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [sheetsRes, ahrefsRes] = await Promise.all([
        fetch(`${API}/sheets-tool/sheets`, { credentials: "include" }),
        fetch(`${API}/sheets-tool/ahrefs`, { credentials: "include" })
      ]);
      if (sheetsRes.ok) setSheets(await sheetsRes.json());
      if (ahrefsRes.ok) setAhrefsSheets(await ahrefsRes.json());
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSheetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingSheet(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/sheets-tool/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        toast.success("Archivo de URLs subido");
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error subiendo archivo");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setUploadingSheet(false);
    e.target.value = "";
  };

  const handleAhrefsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingAhrefs(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/sheets-tool/upload-ahrefs`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        toast.success("Archivo Ahrefs subido");
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error subiendo archivo");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setUploadingAhrefs(false);
    e.target.value = "";
  };

  const handleMerge = async () => {
    if (!selectedSheet || !selectedAhrefs) {
      toast.error("Selecciona ambos archivos");
      return;
    }
    
    setMerging(true);
    try {
      const response = await fetch(`${API}/sheets-tool/merge-ahrefs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          sheet_id: selectedSheet,
          ahrefs_sheet_id: selectedAhrefs,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Combinados ${data.matched_count} de ${data.total_urls} URLs`);
        await downloadWithAuth(
          `${API}/sheets-tool/results/${data.result_id}/export-excel`,
          `ahrefs_merge_${new Date().toISOString().split('T')[0]}.xlsx`
        );
      } else {
        toast.error(data.detail || "Error en la combinación");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setMerging(false);
  };

  const handleSortByTraffic = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setSorting(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/sheets-tool/sort-by-traffic`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Ordenadas ${data.result_count} filas por tráfico`);
        await downloadWithAuth(
          `${API}/sheets-tool/results/${data.result_id}/export-excel`,
          `ordenado_trafico_${new Date().toISOString().split('T')[0]}.xlsx`
        );
      } else {
        toast.error(data.detail || "Error ordenando");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setSorting(false);
    e.target.value = "";
  };

  const handleDeleteSheet = async (sheetId, isAhrefs = false) => {
    if (!confirm("¿Eliminar este archivo?")) return;
    
    try {
      const response = await fetch(`${API}/sheets-tool/sheet/${sheetId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Archivo eliminado");
        loadData();
        if (selectedSheet === sheetId) setSelectedSheet("");
        if (selectedAhrefs === sheetId) setSelectedAhrefs("");
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ahrefs-integration-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Integración Ahrefs</h2>
          <p className="text-muted-foreground">Combinar y ordenar datos de Ahrefs con tus archivos de URLs</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-purple-500/5 border-purple-500/20">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Database className="w-8 h-8 text-purple-500 shrink-0" />
            <div className="text-sm space-y-2">
              <p className="font-semibold text-purple-600 dark:text-purple-400">¿Qué hace esta herramienta?</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Combina tu archivo de URLs con datos exportados de Ahrefs</li>
                <li>Añade columnas de DR (Domain Rating) y Tráfico a tus URLs</li>
                <li>Ordena los resultados por tráfico (de mayor a menor)</li>
                <li>Preserva todas las columnas originales de tu archivo</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Merge Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-5 h-5 text-purple-500" />
            Combinar con Ahrefs
          </CardTitle>
          <CardDescription>
            Une tu archivo de URLs con los datos de DR y Tráfico de Ahrefs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* URL Sheet Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Tu archivo de URLs</label>
              <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar archivo" />
                </SelectTrigger>
                <SelectContent>
                  {sheets.map((sheet) => (
                    <SelectItem key={sheet.sheet_id} value={sheet.sheet_id}>
                      {sheet.filename} ({sheet.entry_count} filas)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <input
                  id="sheet-upload"
                  type="file"
                  className="hidden"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleSheetUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById("sheet-upload")?.click()}
                  disabled={uploadingSheet}
                >
                  {uploadingSheet ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Subir URLs
                </Button>
              </div>
            </div>
            
            {/* Ahrefs Sheet Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Archivo de Ahrefs</label>
              <Select value={selectedAhrefs} onValueChange={setSelectedAhrefs}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar Ahrefs" />
                </SelectTrigger>
                <SelectContent>
                  {ahrefsSheets.map((sheet) => (
                    <SelectItem key={sheet.sheet_id} value={sheet.sheet_id}>
                      {sheet.filename} ({sheet.entry_count} filas)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <input
                  id="ahrefs-upload"
                  type="file"
                  className="hidden"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleAhrefsUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById("ahrefs-upload")?.click()}
                  disabled={uploadingAhrefs}
                >
                  {uploadingAhrefs ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Subir Ahrefs
                </Button>
              </div>
            </div>
          </div>

          {/* Uploaded Files List */}
          {(sheets.length > 0 || ahrefsSheets.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              {sheets.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Archivos de URLs ({sheets.length})</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {sheets.map((s) => (
                      <div key={s.sheet_id} className="flex items-center justify-between text-xs p-2 bg-muted/50 rounded">
                        <span className="truncate flex-1">{s.filename}</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeleteSheet(s.sheet_id)}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {ahrefsSheets.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Archivos Ahrefs ({ahrefsSheets.length})</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {ahrefsSheets.map((s) => (
                      <div key={s.sheet_id} className="flex items-center justify-between text-xs p-2 bg-muted/50 rounded">
                        <span className="truncate flex-1">{s.filename}</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeleteSheet(s.sheet_id, true)}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <Button 
            onClick={handleMerge}
            disabled={!selectedSheet || !selectedAhrefs || merging}
            className="w-full"
          >
            {merging ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Database className="w-4 h-4 mr-2" />
            )}
            Combinar y Descargar Excel
          </Button>
        </CardContent>
      </Card>

      {/* Sort by Traffic Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowDownUp className="w-5 h-5 text-blue-500" />
            Ordenar por Tráfico
          </CardTitle>
          <CardDescription>
            Sube un archivo con columna "Traffic" y lo ordenará de mayor a menor
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input
            id="sort-upload"
            type="file"
            className="hidden"
            accept=".csv,.xlsx,.xls"
            onChange={handleSortByTraffic}
          />
          <Button
            variant="outline"
            className="w-full"
            onClick={() => document.getElementById("sort-upload")?.click()}
            disabled={sorting}
          >
            {sorting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Ordenando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Subir y Ordenar por Tráfico
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AhrefsIntegrationTab;
